/**
 * Inbox OAuth Callback Handler
 * 
 * This utility handles the OAuth callback for the inbox integration.
 * Developers must call this function on their OAuth redirect page.
 * 
 * Usage:
 * ```tsx
 * // On your callback page (e.g., /oauth/callback)
 * import { handleInboxCallback } from '@cas-parser/connect';
 * 
 * useEffect(() => {
 *   handleInboxCallback();
 * }, []);
 * ```
 * 
 * Or for vanilla JS:
 * ```html
 * <script>
 *   PortfolioConnect.handleInboxCallback();
 * </script>
 * ```
 */

export interface InboxCallbackResult {
  success: boolean;
  email?: string;
  error?: string;
}

export interface InboxOAuthMessage {
  type: 'INBOX_OAUTH_CALLBACK';
  success: boolean;
  inbox_token?: string;
  email?: string;
  error?: string;
  state?: string;
}

/**
 * Handles the OAuth callback on the redirect page.
 * Reads URL parameters and sends them to the opener window via postMessage.
 * Closes the popup window after sending the message.
 * 
 * @returns The callback result with success status and email/error
 */
export function handleInboxCallback(): InboxCallbackResult {
  const urlParams = new URLSearchParams(window.location.search);
  const inboxToken = urlParams.get('inbox_token');
  const email = urlParams.get('email');
  const error = urlParams.get('error');
  const state = urlParams.get('state');

  const message: InboxOAuthMessage = {
    type: 'INBOX_OAUTH_CALLBACK',
    success: !error && !!inboxToken && !!email,
    inbox_token: inboxToken || undefined,
    email: email || undefined,
    error: error || undefined,
    state: state || undefined,
  };

  // Try to send message to opener (popup flow)
  if (window.opener && !window.opener.closed) {
    try {
      // Attempt to use the opener's origin for security; fall back to '*'
      // if cross-origin restrictions prevent access.
      let targetOrigin = '*';
      try {
        targetOrigin = window.opener.location.origin || '*';
      } catch (_) {
        // Cross-origin — '*' is acceptable here because the message
        // contains only an opaque token and email, no secrets.
      }
      window.opener.postMessage(message, targetOrigin);

      // Brief delay so postMessage flushes before the popup tears down.
      setTimeout(() => {
        window.close();
      }, 100);

      return {
        success: message.success,
        email: message.email,
        error: message.error,
      };
    } catch (e) {
      console.error('[CAS Connect] Failed to send OAuth message to opener:', e);
    }
  }

  // Fallback: If no opener (e.g. iOS Chrome opens popups as tabs),
  // store in sessionStorage. The InboxMode component on the original tab
  // will pick this up via its visibilitychange listener when the user
  // switches back. We intentionally do NOT redirect here — that would
  // create a second tab of the app with the modal closed, which is
  // confusing. The callback page shows a "Return to App" fallback button.
  if (message.success && inboxToken) {
    sessionStorage.setItem('cas_connect_inbox_token', inboxToken);
    sessionStorage.setItem('cas_connect_inbox_email', email || '');
  } else if (error) {
    sessionStorage.setItem('cas_connect_inbox_error', error);
  }

  return {
    success: message.success,
    email: message.email,
    error: message.error,
  };
}

/** True when the current URL looks like an OAuth callback (has `inbox_token` or `error`). */
export function isInboxCallbackPage(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('inbox_token') || urlParams.has('error');
}

/** Discriminator value for the postMessage payload between popup and opener. */
export const INBOX_OAUTH_MESSAGE_TYPE = 'INBOX_OAUTH_CALLBACK';
